<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ t('general.contacts') }}
        </h6>

        <CreateButton
            :label="t('address_book.create_company')"
            :to="{ name: 'addressBookCompanyForm' }"/>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ t('address_book.company') }}
        </h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            @search="getCompanies"/>

        <PerPage :values="[10, 25, 50, 100]" @update:perPage="getCompanies"/>

        <ExcelExportButton
            style="margin-right: 15px;"
            :label="$t('general.excel_export')"
            :loading="isExcelLoaded"
            @click="getExcelExport"/>

        <EmptyState
            v-if="!companies?.data?.length"
            icon="fa-address-book"
            title="Keine Kontakte gefunden"
            subtitle="Es wurden keine Kontakte gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>
                <a href="#" @click="sortBy('firstName')">
                  <span>{{ t('general.first_name') }}</span>
                  <i :class="getIconClass('firstName')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('lastName')">
                  <span>{{ t('general.last_name') }}</span>
                  <i :class="getIconClass('lastName')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('company')">
                  <span>{{ t('general.company_client') }}</span>
                  <i :class="getIconClass('company')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('phoneNumber')">
                  {{ t('general.phone_number') }}
                  <i :class="getIconClass('phoneNumber')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('email')">
                  {{ t('general.email') }}
                  <i :class="getIconClass('email')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('address')">
                  {{ t('general.street') }}
                  <i :class="getIconClass('address')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('zip')">
                  {{ t('general.postal_code') }}
                  <i :class="getIconClass('zip')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('city')">
                  {{ t('general.city') }}
                  <i :class="getIconClass('city')"></i>
                </a>
              </th>
              <th>
                <a href="#" @click="sortBy('position')">
                  {{ t('general.position') }}
                  <i :class="getIconClass('position')"></i>
                </a>
              </th>
              <th>
                {{ t('general.info') }}
              </th>
              <th style="min-width: 130px!important; text-align: center">
                {{ t('general.actions') }}
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(company) in companies?.data" :key="company.id">
              <td>{{ company?.firstName }}</td>
              <td>{{ company?.lastName }}</td>
              <td>{{ company?.company }}</td>
              <td>{{ company?.phoneNumber }}</td>
              <td>{{ company?.email }}</td>
              <td>{{ company?.address }}</td>
              <td>{{ company?.zip }}</td>
              <td>{{ company?.city }}</td>
              <td>{{ company?.position }}</td>
              <td>{{ company?.info ? 'JA' : 'NEIN' }}</td>
              <td class="text-end">
                <button class="btn btn-primary"
                        @click="router.push({name: 'addressBookCompanyForm', params: {id: company?.id} })">
                  <i class="os-icon os-icon-edit"></i>
                </button>
                <button class="btn btn-danger ms-2"
                        @click="deleteModal(company.id!)">
                  <i class="os-icon os-icon-trash"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <Pagination
            v-if="companies?.pagination?.total_pages ?? 0 > 1"
            :totalPages="companies?.pagination?.total_pages"
            :currentPage="companies?.pagination?.current_page"
            :totalItems="companies?.pagination?.total_items"
            :perPage="companies?.pagination?.items_per_page"
            @pagechanged="getCompanies"
        />
      </div>
    </div>

    <ModalDelete
        v-model="showModal"
        :header="t('general.delete_item_header')"
        :onDelete="confirmDeleteCompany">
    </ModalDelete>

  </PageTemplate>
</template>

<script setup lang="ts">
import companyService from '@/api/AddressBook/company';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {downloadFile, getSortIconClass} from '@/shared/functions';
import {onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import Search from '../Search.vue';
import {IAddressBookFilters, IAddressBookList} from "@/interfaces/AddressBook/IAddressBook";
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const {t} = useI18n();
const companies = ref<IAddressBookList>();
const filters = ref<IAddressBookFilters>({
  firstName: '',
  lastName: '',
  city: '',
  company: '',
  phoneNumber: '',
  position: '',
  email: '',
  orderBy: 'DESC',
  sortBy: '',
  page: 1,
  limit: 10,
});

const showModal = ref(false);
const companyId = ref<number | null>(null);
const isSearching = ref(false);
const isLoaded = ref(false);
const isExcelLoaded = ref(false);

async function getCompanies(
    page: number | undefined = 1,
    perPage?: number,
    passedFilters?: IAddressBookFilters
) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = companies.value?.pagination?.items_per_page;

  try {
    const {data} = await companyService.getCompanies({
      ...(passedFilters ? passedFilters : filters.value),
      page: page,
      limit: perPage ?? perPageRes!,
    });
    companies.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function confirmDeleteCompany() {
  try {
    await companyService.deleteCompany(companyId.value!);
    companyId.value = null;
    showModal.value = false;
    await getCompanies();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

function getIconClass(key: string) {
  return getSortIconClass(filters.value, key);
}

function sortBy(key: string) {
  filters.value = {
    ...filters.value,
    sortBy: key,
    orderBy: filters.value.orderBy === 'DESC' || filters.value.sortBy !== key ? 'ASC' : 'DESC',
  };
  getCompanies();
}

function deleteModal(id: number) {
  CloseToast();
  companyId.value = id;
  showModal.value = true;
}

async function getExcelExport() {
  await downloadFile('/address-book/company/excel-export', undefined,
      filters.value,
      false,
      "GET",
      isExcelLoaded
  )
}

onMounted(async () => {
  CloseToast()
  await getCompanies();

  isLoaded.value = true;
});
</script>

<style scoped>

</style>